import { Overlay } from '@angular/cdk/overlay';
import { APP_BASE_HREF } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { importProvidersFrom } from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SELECT_SCROLL_STRATEGY } from '@angular/material/select';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { provideAnimations } from '@angular/platform-browser/animations';
import { NoPreloading, provideRouter, withInMemoryScrolling, withPreloading, withRouterConfig } from '@angular/router';
import { environment } from '@environments/environment';
import {
    provideBrainTab,
    provideGTM,
    provideMaze,
    provideOnetrustCookieManager,
    providePendoAnalytics,
    provideQualtricsFeedbackSurvey,
} from '@injectors';
import {
    ContentInterceptor,
    KeplerCoveoInterceptor,
    KeplerCredlyInterceptor,
    KeplerDbInterceptor,
    KeplerDoceboInterceptor,
    KeplerHigherLogicInterceptor,
    KeplerMindpalaceInterceptor,
    KeplerPurecloudInterceptor,
    KeplerSfdcInterceptor,
    KeplerSlapfiveInterceptor,
    KeplerSnowflakeInterceptor,
    PhoenixOktaInterceptor,
} from '@interceptors';
import { OktaAuthModule } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { HIGHLIGHT_OPTIONS } from 'ngx-highlightjs';
import { provideCoveoHeadless } from './@shared/injectors/coveo-headless.injector';
import { CommunityInterceptor } from './@shared/interceptors/community.interceptor';
import { gknRouteConfig } from './app-routing.config';
import { GknRoutePath } from './app-routing.enum';

/**
 * Prefer `localStorage` to enable auto-login
 *
 * The following values for storageType are recognized:
 *  - `memory`: values are stored in a closure and will not survive a page reload
 *  - `sessionStorage`: will only be available to the current browser tab.
 *    this means OktaAuth requires the user to still initiate a login flow
 *    with a button click from your app.
 *  - `localStorage`: available to all browser tabs. effectively enables
 *    OktaAuthStateManager to detect login without user interaction.
 *  - `cookie`: available to all browser tabs, and server-side code, but
 *    effectively breaks login for any browser blocking third-party cookies, like Safari.
 *
 * Tokens should be safe in localStorage from XSS attacks because the app's
 * Content Security Policy in customHttp.yml is restrictive (allowlist).
 */
const storageType = 'localStorage';

const oktaAuth = new OktaAuth({
    issuer: environment.oidc.issuer,
    clientId: environment.oidc.clientId,
    redirectUri: environment.oidc.redirectUri,
    scopes: environment.oidc.scopes,
    tokenManager: {
        storage: storageType,
    },
    storageManager: {
        transaction: { storageType },
        cache: { storageType },
        token: { storageType },
    },
    cookies: {
        secure: true, // cookies can be the fallback when local/session storage are not available, so ensure they are secure.
    },
    restoreOriginalUri: async (oktaAuth, originalUri) => {
        // Get all valid route paths from the enum
        const allowedPaths = Object.values(GknRoutePath).map((path) => `/${path}`);

        const isValidPath = (path: string): boolean => {
            const cleanPath = path.split('?')[0].split('#')[0];

            // Check if path matches any allowed route or is a sub-route
            return (
                cleanPath === '/' ||
                cleanPath === '/home' ||
                allowedPaths.some((allowed) => cleanPath === allowed || cleanPath.startsWith(allowed + '/'))
            );
        };

        const isValidUrl = (url: string): boolean => {
            try {
                const urlObj = new URL(url);
                return urlObj.origin === window.location.origin && isValidPath(urlObj.pathname);
            } catch {
                return false;
            }
        };

        let targetUri = '/home';

        if (originalUri && isValidPath(originalUri)) {
            // Keep the full originalUri including query params
            targetUri = originalUri;
        } else if (document.referrer && isValidUrl(document.referrer)) {
            // Keep full URL including query params from referrer
            const referrerUrl = new URL(document.referrer);
            targetUri = referrerUrl.pathname + referrerUrl.search + referrerUrl.hash;
        } else if (window.location.pathname !== '/login/callback' && isValidPath(window.location.pathname)) {
            // Keep current URL with query params
            targetUri = window.location.pathname + window.location.search + window.location.hash;
        }

        window.location.assign(targetUri);
    },
    transformAuthState: async (oktaAuth, authState) => {
        if (!authState.isAuthenticated && authState.error) {
            console.error('Auth error:', authState.error);
        }
        return authState;
    },
});

export const appConfig = {
    providers: [
        provideRouter(
            [
                ...gknRouteConfig,
                {
                    // This route should always be the very last route.
                    // It will catch any non-matching endpoints and redirect to /home.
                    path: '**',
                    pathMatch: 'full',
                    // Do not redirect using HomeComponent or else the
                    // active state won't be set on the navbar (orange top border)
                    redirectTo: '/home',
                    // // component: HomeComponent,
                },
            ],
            withInMemoryScrolling({
                scrollPositionRestoration: 'enabled',
                anchorScrolling: 'enabled',
            }),
            withRouterConfig({ onSameUrlNavigation: 'reload' }),
            withPreloading(NoPreloading),
        ),
        provideHttpClient(
            withInterceptors([
                CommunityInterceptor,
                ContentInterceptor,
                KeplerCoveoInterceptor,
                KeplerCredlyInterceptor,
                KeplerDbInterceptor,
                KeplerDoceboInterceptor,
                KeplerPurecloudInterceptor,
                KeplerSfdcInterceptor,
                KeplerSlapfiveInterceptor,
                KeplerSnowflakeInterceptor,
                KeplerMindpalaceInterceptor,
                KeplerHigherLogicInterceptor,
                PhoenixOktaInterceptor,
            ]),
        ),
        provideAnimations(),
        {
            provide: APP_BASE_HREF,
            useValue: environment.appBaseHref,
        },
        /** Start Orchestrators portal providers */
        importProvidersFrom(OktaAuthModule.forRoot({ oktaAuth })),
        provideBrainTab(),
        provideCoveoHeadless(),
        provideGTM(),
        provideMaze(),
        provideOnetrustCookieManager(),
        providePendoAnalytics(),
        provideQualtricsFeedbackSurvey(),
        /** End Orchestrators portal providers */
        /** Start Angular Material options */
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: {
                subscriptSizing: 'dynamic',
                appearance: 'outline',
                defaultBorderRadius: '8px',
            },
        },
        {
            // Scrolling is disabled while this select component is opened to avoid
            // z-index issues with the rest of the site, most notably the sticky header.
            provide: MAT_SELECT_SCROLL_STRATEGY,
            useFactory: (overlay: Overlay) => () => overlay.scrollStrategies.block(),
            deps: [Overlay],
        },
        {
            provide: MAT_TABS_CONFIG,
            useValue: {
                animationDuration: '0ms',
                dynamicHeight: true,
                disablePagination: true,
                disableRipple: true,
            },
        },
        /** End Angular Material options */
        /** syntax highlighting library used on the /status page */
        {
            // TODO Update to `provideHighlightOptions` in newer version when this app is angular 17+
            provide: HIGHLIGHT_OPTIONS,
            useValue: {
                coreLibraryLoader: () => import('highlight.js/lib/core'),
                lineNumbersLoader: () => import('ngx-highlightjs/line-numbers'), // Optional, only if you want the line numbers
                languages: {
                    javascript: () => import('highlight.js/lib/languages/javascript'),
                    json: () => import('highlight.js/lib/languages/json'),
                },
            },
        },
    ],
};
